import { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";

import { paper, fonts, shade } from "../theme";
import { MsCircleBtn } from "../ms";
import { useGameState } from "../GameStateContext";
import { useWebSocket } from "../useWebSocket";
import HoldToConfirmButton from "./HoldToConfirmButton";

const Wrapper = styled.div`
    position: fixed;
    top: calc(var(--safe-top) + 12px);
    right: calc(var(--safe-right) + 12px);
    z-index: 999;
`;

/* The cog: the ink circle press, wearing the serif glyph. */
const Trigger = styled(MsCircleBtn)`
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 800;
    font-size: 18px;
`;

const Popup = styled.div`
    position: absolute;
    top: 48px;
    right: 0;
    background: ${paper.paperCool};
    border-radius: 8px;
    box-shadow:
        0 0 0 1px ${shade(0.08)},
        0 18px 36px -12px ${shade(0.45)};
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 200px;
`;

const Title = styled.div`
    font-family: ${fonts.sans};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${paper.inkSoft};
    border-bottom: 1px dashed ${shade(0.18)};
    padding-bottom: 8px;
`;

const Item = styled.button`
    padding: 10px 12px;
    background: transparent;
    border: 0;
    border-radius: 6px;
    font-family: ${fonts.sans};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    text-align: left;
    color: ${paper.ink};
    transition: background 0.16s;

    &:hover { background: ${shade(0.06)}; }
`;

interface Props {
    /** Opens the in-game roster. App owns that state so the sheet can replace
     *  the phase screen instead of covering it — an opaque sheet would occlude
     *  the app's one global backdrop. */
    onOpenRoster: () => void;
}

export default function HostControls({ onOpenRoster }: Props) {
    const { state } = useGameState();
    const { sendMessage } = useWebSocket();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    // Escape closes and hands focus back to the cog; a pointer press anywhere
    // outside just closes. Bound only while open, so we aren't listening on the
    // document for the whole game.
    useEffect(() => {
        if (!open) return;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            setOpen(false);
            triggerRef.current?.focus();
        };
        const onPointerDown = (e: PointerEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        };

        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("pointerdown", onPointerDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("pointerdown", onPointerDown);
        };
    }, [open]);

    if (!state.self?.isHost) return null;
    if (state.phase === "waiting_room") return null;

    const handleReturn = () => {
        sendMessage({ type: "return_to_lobby" });
        setOpen(false);
    };

    return (
        <Wrapper ref={wrapRef}>
            {/* `title` is a tooltip, not an accessible name — it left the only
                host affordance in the game unlabelled to a screen reader. */}
            <Trigger
                ref={triggerRef}
                type="button"
                tone="ink"
                size={38}
                onClick={() => setOpen(o => !o)}
                aria-label="Host controls"
                aria-expanded={open}
            >⚙</Trigger>
            {open && (
                /* Deliberately a labelled group rather than role="menu": a menu
                   promises arrow-key roving that these two plain buttons don't
                   implement, and a broken promise reads worse than none. */
                <Popup role="group" aria-label="Host controls">
                    <Title>Host</Title>
                    <Item type="button" onClick={() => { onOpenRoster(); setOpen(false); }}>Manage roster</Item>
                    <HoldToConfirmButton
                        text="Hold to end game"
                        activeText="Keep holding…"
                        onConfirm={handleReturn}
                        variant="red"
                    />
                </Popup>
            )}
        </Wrapper>
    );
}
